import React, { useState } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { ArrowBack, Movie, LightMode, DarkMode } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';
import { useTheme } from '@mui/material/styles';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { HypeCircleLogo } from '../Branding';

const ForgotPassword = () => {
  const { addNotification, toggleTheme, themeMode } = useApp();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [step, setStep] = useState('request'); // 'request' or 'confirm'
  const [email, setEmail] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateEmail = (email) => {
    if (!email?.trim()) return 'Email is required';
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email.trim())) return 'Please enter a valid email address';
    return '';
  };

  const validatePassword = (password) => {
    if (!password) return 'Password is required';
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (!/(?=.*[a-z])/.test(password)) return 'Password must include a lowercase letter';
    if (!/(?=.*[A-Z])/.test(password)) return 'Password must include an uppercase letter';
    if (!/(?=.*[0-9])/.test(password)) return 'Password must include a number';
    if (!/(?=.*[^A-Za-z0-9])/.test(password)) return 'Password must include a special character';
    return '';
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    
    const emailError = validateEmail(email);
    if (emailError) {
      setErrors({ email: emailError });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await resetPassword({ username: email.trim() });
      setStep('confirm');
      addNotification({
        type: 'success',
        message: 'Password reset code sent to your email!',
      });
    } catch (error) {
      console.error('Reset password error:', error);
      
      let errorMessage = 'Failed to send reset code. Please try again.';
      
      switch (error.name) {
        case 'UserNotFoundException':
          errorMessage = 'No account found with this email address.';
          break;
        case 'LimitExceededException':
          errorMessage = 'Too many requests. Please wait before trying again.';
          break;
        case 'InvalidParameterException':
          errorMessage = 'Please enter a valid email address.';
          break;
        default:
          errorMessage = error.message || errorMessage;
      }

      setErrors({ api: errorMessage });
      addNotification({
        type: 'error',
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();

    const codeError = !confirmationCode.trim() ? 'Confirmation code is required' : '';
    const passwordError = validatePassword(newPassword);
    const confirmError = newPassword !== confirmPassword ? 'Passwords do not match' : '';

    if (codeError || passwordError || confirmError) {
      setErrors({
        confirmationCode: codeError,
        newPassword: passwordError,
        confirmPassword: confirmError,
      });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await confirmResetPassword({
        username: email.trim(),
        confirmationCode: confirmationCode.trim(),
        newPassword,
      });

      addNotification({
        type: 'success',
        message: 'Password reset successfully! You can now sign in.',
      });
      
      navigate('/auth', { replace: true });
    } catch (error) {
      console.error('Confirm reset error:', error);
      
      let errorMessage = 'Failed to reset password. Please try again.';
      
      switch (error.name) {
        case 'CodeMismatchException':
          errorMessage = 'Invalid confirmation code. Please check and try again.';
          break;
        case 'ExpiredCodeException':
          errorMessage = 'Confirmation code has expired. Please request a new one.';
          break;
        case 'InvalidPasswordException':
          errorMessage = 'Password does not meet requirements.';
          break;
        default:
          errorMessage = error.message || errorMessage;
      }

      setErrors({ api: errorMessage });
      addNotification({
        type: 'error',
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setLoading(true);
      await resetPassword({ username: email.trim() });
      addNotification({
        type: 'success',
        message: 'New reset code sent to your email!',
      });
    } catch (error) {
      addNotification({
        type: 'error',
        message: 'Failed to resend code. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  if (step === 'confirm') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: theme.spacing(2),
          position: 'relative',
        }}
      >
        {/* Top Right Buttons */}
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 1000,
            display: 'flex',
            gap: 1,
          }}
        >
          <IconButton
            onClick={() => navigate('/movies')}
            title="Browse Movies"
            sx={{
              bgcolor: 'rgba(0, 0, 0, 0.3)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.4)',
              '&:hover': { 
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                transform: 'scale(1.05)'
              },
            }}
          >
            <Movie sx={{ color: 'white' }} />
          </IconButton>
          <IconButton
            onClick={toggleTheme}
            title="Toggle Theme"
            sx={{
              bgcolor: 'rgba(0, 0, 0, 0.3)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.4)',
              '&:hover': { 
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                transform: 'scale(1.05)'
              },
            }}
          >
            {themeMode === 'dark' ? (
              <LightMode sx={{ color: 'white' }} />
            ) : (
              <DarkMode sx={{ color: 'white' }} />
            )}
          </IconButton>
        </Box>

        <Card sx={{ 
          maxWidth: { xs: '100%', sm: 450 }, 
          width: '100%', 
          borderRadius: { xs: 0, sm: 3 }, 
          boxShadow: { xs: 0, sm: 3 },
          backdropFilter: 'blur(20px)',
          background: theme.palette.mode === 'dark'
            ? 'rgba(30, 30, 30, 0.9)'
            : 'rgba(255, 255, 255, 0.95)',
        }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box textAlign="center" mb={3}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <HypeCircleLogo size="lg" variant="default" />
            </Box>
            <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
              Reset Password
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Enter the code sent to <strong>{email}</strong> and your new password
            </Typography>
          </Box>

          {errors.api && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.api}
            </Alert>
          )}

          <Box component="form" onSubmit={handleConfirmReset}>
            <TextField
              fullWidth
              label="Confirmation Code"
              value={confirmationCode}
              onChange={(e) => setConfirmationCode(e.target.value)}
              error={!!errors.confirmationCode}
              helperText={errors.confirmationCode}
              disabled={loading}
              autoComplete="one-time-code"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              error={!!errors.newPassword}
              helperText={errors.newPassword}
              disabled={loading}
              autoComplete="new-password"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              disabled={loading}
              autoComplete="new-password"
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ mb: 2, py: 1.5 }}
            >
              {loading ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={16} color="inherit" />
                  Resetting Password...
                </Box>
              ) : (
                'Reset Password'
              )}
            </Button>

            <Box textAlign="center">
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Didn't receive the code?
              </Typography>
              <Button
                variant="text"
                onClick={handleResendCode}
                disabled={loading}
                size="small"
              >
                Resend Code
              </Button>
            </Box>
          </Box>

          <Box textAlign="center" mt={3}>
            <Link
              component={RouterLink}
              to="/auth"
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}
            >
              <ArrowBack sx={{ mr: 1, fontSize: 16 }} />
              Back to Sign In
            </Link>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(2),
        position: 'relative',
      }}
    >
      {/* Top Right Buttons */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1000,
          display: 'flex',
          gap: 1,
        }}
      >
        <IconButton
          onClick={() => navigate('/movies')}
          title="Browse Movies"
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
          }}
        >
          <Movie sx={{ color: 'white' }} />
        </IconButton>
        <IconButton
          onClick={toggleTheme}
          title="Toggle Theme"
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
          }}
        >
          {themeMode === 'dark' ? (
            <LightMode sx={{ color: 'white' }} />
          ) : (
            <DarkMode sx={{ color: 'white' }} />
          )}
        </IconButton>
      </Box>

      <Card sx={{ 
        maxWidth: { xs: '100%', sm: 450 }, 
        width: '100%', 
        borderRadius: { xs: 0, sm: 3 }, 
        boxShadow: { xs: 0, sm: 3 },
        backdropFilter: 'blur(20px)',
        background: theme.palette.mode === 'dark'
          ? 'rgba(30, 30, 30, 0.9)'
          : 'rgba(255, 255, 255, 0.95)',
      }}>
      <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Box textAlign="center" mb={3}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <HypeCircleLogo size="lg" variant="default" />
          </Box>
          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            Forgot Password?
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Enter your email address and we'll send you a code to reset your password
          </Typography>
        </Box>

        {errors.api && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.api}
          </Alert>
        )}

        <Box component="form" onSubmit={handleRequestReset}>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
            disabled={loading}
            autoComplete="email"
            autoFocus
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{ mb: 3, py: 1.5 }}
          >
            {loading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} color="inherit" />
                Sending Code...
              </Box>
            ) : (
              'Send Reset Code'
            )}
          </Button>
        </Box>

        <Box textAlign="center">
          <Link
            component={RouterLink}
            to="/auth"
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}
          >
            <ArrowBack sx={{ mr: 1, fontSize: 16 }} />
            Back to Sign In
          </Link>
        </Box>
      </CardContent>
    </Card>
  </Box>
  );
};

export default ForgotPassword;